import { Injectable } from '@angular/core';
import * as AWS from 'aws-sdk';
import { Document, Metadata } from 'src/app/beans/health-record/other-documents.bean';
import { ModalService } from '../../core/modal.service';
import { ListDocumentService } from '../../../services/health-record/other-documents/list-document.service';
import { SpinnerComponent } from '../../../modules/shared/spinner/spinner.component';
import { Environment } from '../../../config/environment';
import saveAs from 'file-saver';
import { Subject, from, Observable } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class S3Service {
	bucketName = this.environment.getEnvironment().S3_BUCKET_NAME;
	epmsPID: any;
	folderId: any;
	documents: Document[];
	preSignedUrl: Subject<string> = new Subject<string>();
	constructor(
		// private authService: AuthService,
		// private jwtService: JwtHelper,
		private modalService: ModalService,
		private listService: ListDocumentService,
		private spinner: SpinnerComponent,
		private environment: Environment
	) {
		this.initialize();
	}

	/**
	 * Set up the S3 service for the currently logged in user
	 * We need to invoke this upon each new login event
	 * to set the folder Id to that of the newly logged in user
	 */
	initialize(callback?: () => void): void {
		// // Gets epms PID from jwt service
		// this.epmsPID = this.jwtService.getEpmsId();
		// // Gets temporary credentials of user
		// this.authService.getAwsCredentials();
		// // Getting federated identity id (which will be used as s3 folder id of user) from federated identities
		// const val = JSON.stringify(AWS.config.credentials);
		// const parsedVal = JSON.parse(val);
		// let identityId = '';
		// if (parsedVal !== null && parsedVal !== undefined) {
		//   const { params } = parsedVal;
		//   if (params !== null && params !== undefined) {
		//     const { IdentityId } = params;
		//     identityId = IdentityId;
		//   }
		// }
		// this.folderId = `${identityId}/`;
		// console.log('folderId:', this.folderId);
		// Initializing s3 object

		if (typeof callback === 'function') {
			callback.call(null);
		}
	}
	/**
	 * Lists objects in s3 bucket
	 */
	listObjects(): Observable<any> {
		const promise = new Promise((listResolve, listReject) => {
			const listObjectParams = {
				Bucket: this.bucketName,
				Prefix: this.folderId,
			};
			const self = this;
			const promises = [];
			const s3 = new AWS.S3({ signatureVersion: 'v4' });
			s3.listObjects(listObjectParams, function (err, files) {
				if (err) {
					self.modalService.showToaster();
					listReject(err);
				} else {
					self.documents = [];
					files.Contents.forEach(file => {
						if (file.Key !== self.folderId) {
							const getObjectParams = {
								Bucket: self.bucketName,
								Key: file.Key,
							};
							promises.push(self.getObject(getObjectParams, file.Key));
						}
					});
					Promise.all(promises).then(
						res => {
							listResolve(res);
						},
						error => {
							listReject(error);
						}
					);
				}
			});
		});
		return from(promise);
	}

	/**
	 * Gets s3 document by name
	 * @param params
	 * @param key
	 */
	getObject(params, key): Promise<any> {
		return new Promise((resolve, reject) => {
			const self = this;
			const s3 = new AWS.S3({ signatureVersion: 'v4' });
			s3.getObject(params, function (error, object) {
				const document = new Document();
				if (error) {
					self.modalService.showToaster();
					reject(error);
				}
				document.key = key;
				if (object.Metadata && object.Metadata.metadata) {
					document.metadata = JSON.parse(object.Metadata.metadata);
				}
				document.body = object.Body;
				resolve(document);
			});
		});
	}

	/**
	 * Adds object to s3 bucket
	 * @param file
	 * @param metadata
	 */
	uploadObject(file, metadata: Metadata) {
		const self = this;
		const uploadObjectParams = {
			Bucket: this.bucketName,
			Body: file,
			Key: this.folderId + file.name,
			Metadata: { metadata: JSON.stringify(metadata) },
		};
		this.spinner.startLoader();
		const s3 = new AWS.S3({ signatureVersion: 'v4' });
		s3.upload(uploadObjectParams, function (err, data) {
			if (err) {
				self.spinner.stopLoader();
				self.modalService.showToaster();
			} else {
				self.spinner.stopLoader();
				self.modalService.openInfoModal('SUCCESS', 'success', 'Document uploaded successfully', 'OK');
				self.modalService.getResult().subscribe(res => {
					if (res.value === 'ok') {
						self.listObjects().subscribe(
							result => {
								self.listService.setDocuments(result);
							},
							error => { }
						);
					}
				});
			}
		});
	}

	/**
	 * Downloads the document
	 * @param data
	 * @param filename
	 * @param fileType
	 */
	download(data, filename, fileType) {
		const blob = new Blob([new Uint8Array(data)], { type: fileType });
		saveAs(blob, filename, fileType);
	}

	/**
	 * Deletes object by object key from s3
	 * @param key
	 */
	deleteObject(key) {
		const self = this;
		const deleteObjectParams = {
			Bucket: this.bucketName,
			Key: key,
		};
		this.spinner.startLoader();
		const s3 = new AWS.S3({ signatureVersion: 'v4' });
		s3.deleteObject(deleteObjectParams, function (err, data) {
			if (err) {
				self.spinner.stopLoader();
				self.modalService.showToaster();
			} else {
				self.spinner.stopLoader();
				self.modalService.openInfoModal('SUCCESS', 'success', 'Document deleted successfully', 'OK');
				self.modalService.getResult().subscribe(res => {
					if (res.value === 'ok') {
						self.listObjects().subscribe(
							result => {
								self.listService.setDocuments(result);
							},
							error => { }
						);
					}
				});
			}
		});
	}

	/**
	 * Gets pre-signed url for getting feeds from s3 auth bucket
	 * @param fileName
	 */
	setPreSignedUrl(fileName) {
		const bucketName = this.environment.getEnvironment().DASHBOARD_STATIC_CONTENT_BUCKET;
		const params = { Bucket: bucketName, Key: fileName };
		const self = this;
		const s3 = new AWS.S3({ signatureVersion: 'v4' });
		s3.getSignedUrl('getObject', params, function (err, data) {
			if (data) {
				self.preSignedUrl.next(data);
			} else {
				self.preSignedUrl.error(err);
				console.error(`[${S3Service.name}][${self.setPreSignedUrl.name}]`, err);
			}
		});
	}

	/**
	 * Getting pre-signed url with aws access credentials
	 */
	getPreSignedUrl(): Observable<any> {
		return this.preSignedUrl.asObservable();
	}

	/**
	 * Getting image as ObjectURL or Base64 DataURI
	 * @param fileKey Image file key
	 * @param base64Body If true, it'll convert Image File's body to Base64 String, else default is a Blob Object URL
	 * @returns Promise<string> - either (ObjectURL or Base64 DataURI) of the image
	 */
	getImageAsBytes(fileKey: string, base64Body: boolean = false): Promise<string> {
		const bucketName = this.environment.getEnvironment().S3_FEED_IMAGE_BUCKET as string;
		return new Promise((resolve: (AWS_Credentials: string) => void, reject: (AWS_ServiceError: any) => void) => {
			try {
				fileKey = this.getFileKeyFromImageURL(fileKey, bucketName);
				const getObjectParams = {
					Bucket: bucketName,
					Key: fileKey,
				};
				const s3 = new AWS.S3({ signatureVersion: 'v4' });
				s3.getObject(getObjectParams, (err, file: AWS.S3.GetObjectOutput) => {
					if (err) {
						reject({
							step: `While trying fetch image ${fileKey} from ${bucketName}`,
							error: err,
						});
						return;
					}
					if (base64Body) {
						try {
							resolve(`data:${file.ContentType};base64,${(file.Body as Buffer).toString('base64')}`);
						} catch (err_base64) {
							throw {
								step: 'While trying to convert to base64',
								error: err_base64,
							};
						}
					} else {
						try {
							const BLOB = new Blob([(file.Body as Uint8Array).buffer], {
								type: file.ContentType,
							});
							resolve(URL.createObjectURL(BLOB));
						} catch (err_objurl) {
							throw {
								step: 'While trying to convert to ObjectURL',
								error: err_objurl,
							};
						}
					}
				});
			} catch (error) {
				reject({ error: error });
			}
		});
	}

	/**
	 * Getting file Key from it's URL or Path or Key values
	 * @param fileURL file URL | Path | Key
	 * @param bucketName bucket name to match the URL with
	 * @returns string - fileKey that can be used with getObject(...)
	 */
	getFileKeyFromImageURL(fileURL: string, bucketName: string): string {
		if (!fileURL) {
			throw Error('No Image URL Provided');
		}
		if (fileURL.startsWith('/')) {
			return fileURL.substr(1);
		}
		const slashIndex = fileURL.indexOf('/', bucketName.length);
		if (slashIndex === -1) {
			return fileURL;
		}
		return fileURL.substr(slashIndex + 1);
	}
}
